/** Kept clear above an anchor scrolled into view: the sticky header. */
const HEADER_CLEARANCE_PX = 88;

export const wait = (ms: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, ms));

export function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** The first of these `data-tour` anchors that is on screen. */
export function findAnchor(anchors: string[]): HTMLElement | null {
  for (const anchor of anchors) {
    const element = document.querySelector<HTMLElement>(
      `[data-tour="${anchor}"]`,
    );
    if (element && element.getClientRects().length > 0) return element;
  }
  return null;
}

export async function waitForAnchor(
  anchors: string[],
  timeoutMs: number,
): Promise<HTMLElement | null> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const element = findAnchor(anchors);
    if (element || Date.now() >= deadline) return element;
    await wait(150);
  }
}

/**
 * Resolves once the page has stopped adding and removing nodes for `quietMs` — its data landed —
 * or after `maxMs`. An anchor is often drawn before what it frames: a board shows its question bar
 * while its figures still read 0.
 */
export function waitForQuiet(quietMs = 350, maxMs = 6_000): Promise<void> {
  return new Promise((resolve) => {
    let quietTimer = 0;
    const finish = () => {
      observer.disconnect();
      window.clearTimeout(quietTimer);
      window.clearTimeout(maxTimer);
      resolve();
    };
    const observer = new MutationObserver(() => {
      window.clearTimeout(quietTimer);
      quietTimer = window.setTimeout(finish, quietMs);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    quietTimer = window.setTimeout(finish, quietMs);
    const maxTimer = window.setTimeout(finish, maxMs);
  });
}

/**
 * Scrolls an anchor below the sticky header, a third of the way down so the card fits under it.
 * Done here rather than by Joyride, whose scroll-parent lookup stops at the layout's `overflow-auto`
 * wrapper, which never scrolls.
 */
export async function reveal(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  const bottom = window.innerHeight - 16;
  if (rect.top >= HEADER_CLEARANCE_PX && rect.bottom <= bottom) return;
  const room = bottom - HEADER_CLEARANCE_PX;
  const lead = rect.height < room ? (room - rect.height) * 0.3 : 0;
  const smooth = !prefersReducedMotion();
  window.scrollTo({
    top: window.scrollY + rect.top - HEADER_CLEARANCE_PX - lead,
    behavior: smooth ? "smooth" : "auto",
  });
  if (smooth) await wait(450);
}
